// @noflow
import { ACCOUNT_ROUTES } from '@/routes'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useLocalStorage from '@/hooks/useLocalStorage'

import { DailyCalorieGuidance } from './components/DailyCalorieGuidance'
import { Button } from '@/components/elements/atoms/Button'
import { FixedBase } from '@/components/elements/atoms/FixedBase'
import { WhereToStartModalTrigger } from '@/components/pages/EditDailyAmount/components/WhereToStartModalTrigger'

import STYLES from './EditDailyAmount.module.sass'

import type { EditDailyAmountQuery } from './queries/__generated__/EditDailyAmountQuery'

import {
  LOCAL_STORAGE_KEY,
  PlanChangesLocalStorage
} from '../ReviewPlanChanges/ReviewPlanChanges'
import { createDailyAmountOptionsFromPlans, getDailyAmount } from './utils'

type Props = Partial<EditDailyAmountQuery> & {
  loading: boolean
}

const EditDailyAmountContent = ({
  loading,
  ...props
}: Props): React.JSX.Element | null => {
  const navigate = useNavigate()
  const location = useLocation()
  const [, setPlanChanges] = useLocalStorage<
    string,
    PlanChangesLocalStorage | null
  >(LOCAL_STORAGE_KEY, null)

  const container = useRef<HTMLDivElement>(null)
  const dogs = props.user?.dogs ?? []

  const [chosenPlanId, setChosenPlanId] = useState<string | null>(null)

  /**
   * Manages the current and chosen plan states
   * - Returns null values when loading or no subscription plan
   * - Updates when loading state changes, user data updates, or chosen plan ID changes
   * - Chosen plan defaults to current plan if not found in available plans
   */
  const { currentPlan, chosenPlan } = useMemo(() => {
    if (loading || !props.user?.subscription?.plan) {
      return { currentPlan: null, chosenPlan: null }
    }

    const availablePlans = props.user.availablePlans
    const current = props.user.subscription.plan

    /**
     * We intentionally find plans by comparing their properties (gramsPerDay)
     * instead of direct ID matching because the current subscription plan might not be
     * included in availablePlans. This can happen when a plan was previously available
     * but is no longer offered. By comparing plan properties instead of IDs, we ensure
     * we can still identify and handle the current plan correctly, even if its ID
     * doesn't match any available plan IDs.
     */
    const currentAvailable =
      availablePlans.find(
        (plan) => getDailyAmount(plan) === getDailyAmount(current)
      ) ?? null

    const chosen =
      availablePlans.find((plan) => plan.id === chosenPlanId) ??
      currentAvailable

    return { currentPlan: currentAvailable, chosenPlan: chosen }
  }, [
    loading,
    props.user?.availablePlans,
    props.user?.subscription?.plan,
    chosenPlanId
  ])

  /**
   * Creates daily amount options from available plans
   * - Returns empty array if no available plans
   * - Maps plan data to daily amount option format with localized labels
   * - Recalculates when user data changes
   */
  const dailyAmountOptions = useMemo(
    () => createDailyAmountOptionsFromPlans(props.user?.availablePlans ?? null),
    [props.user?.availablePlans]
  )

  /**
   * Tracks changes between current and chosen plans
   * - Determines if any plan properties have changed to enable/disable update button
   * - Updates only when plans change
   */
  const hasPlanChanged = currentPlan?.id !== chosenPlan?.id

  const handleEditDailyAmount = useCallback((): void => {
    if (!currentPlan || !chosenPlan) return
    setPlanChanges({
      currentPlan,
      chosenPlan
    } as PlanChangesLocalStorage)

    navigate(ACCOUNT_ROUTES.reviewPlanChanges, {
      state: {
        entryPoint: location.state?.entryPoint ?? null
      }
    })
  }, [
    chosenPlan,
    currentPlan,
    navigate,
    setPlanChanges,
    location.state?.entryPoint
  ])

  if (!loading && (!dogs || dogs.length === 0)) {
    return null
  }

  return (
    <div ref={container} className={STYLES.container}>
      <DailyCalorieGuidance
        dailyAmountOptions={dailyAmountOptions}
        currentPlan={currentPlan}
        chosenPlan={chosenPlan}
        setChosenPlanId={setChosenPlanId}
        loading={loading}
        dogs={dogs}
      />
      {dogs && dogs.length > 0 && <WhereToStartModalTrigger data={{ dogs }} />}
      <FixedBase backgroundColor="brandWhite90" container={container}>
        <div className={STYLES.footerContent}>
          <Button
            identifier="edit_daily_amount.update"
            onClick={handleEditDailyAmount}
            typography={{
              text: 'edit_daily_amount.cta.update',
              namespace: 'account'
            }}
            skeleton={{
              isLoading: loading
            }}
            disabled={!hasPlanChanged}
          />
        </div>
      </FixedBase>
    </div>
  )
}

export { EditDailyAmountContent }
